import { JobStatus, Report } from '@/api/generatedApi';
import { Box, CircularProgress, LinearProgress, Paper, Typography, styled } from '@mui/material';
import { AxiosApiError } from '@/types/apiError';
import { useEffect } from 'react';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.background.default,
}));

const Content = styled(Box)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 468,
  textAlign: 'center',
}));

const Loader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const Description = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Status = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(2),
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
  minHeight: 50,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ProgressBar = styled(LinearProgress)({
  width: '100%',
  height: 8,
  borderRadius: 4,
});

const ProgressText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
  fontSize: '0.875rem',
}));

export const GeneratingReport = ({
  report,
  refetch,
  error,
}: {
  report?: Report;
  refetch: () => void | Promise<unknown>;
  error?: AxiosApiError | null;
}) => {
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 2000);
    return () => clearInterval(interval);
  }, [refetch]);

  return (
    <Root data-testid={ReportPageObjectModel.GeneratingReport}>
      <Content>
        <StyledPaper elevation={3}>
          {report?.status === JobStatus.FAILED ? (
            <>
              <Title variant='h5'>Error</Title>
              <Box style={{ textAlign: 'left' }}>
                <ApiErrorAlert
                  error={
                    error ?? {
                      message:
                        'Report failed to generate. Please try again or contact support if the problem persists.',
                    }
                  }
                />
              </Box>
            </>
          ) : (
            <>
              <Loader>
                <CircularProgress size={80} />
              </Loader>
              <Title variant='h5'>Generating Report</Title>
              <Description variant='body1'>
                We&apos;re analyzing your assets to generate your report. This may take a few minutes depending on the
                number and complexity of your models.
              </Description>
              <ProgressContainer>
                {report?.status === JobStatus.RUNNING && report.progress > 0 ? (
                  <>
                    <ProgressBar variant='determinate' value={report.progress} />
                    <ProgressText>{Number(report.progress).toFixed(0)}%</ProgressText>
                  </>
                ) : (
                  <Status variant='body1'>Status: PENDING</Status>
                )}
              </ProgressContainer>
            </>
          )}
        </StyledPaper>
      </Content>
    </Root>
  );
};
